import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { loadClaimEventStatuses, loadClaimEventTypes } from '../../../../../store/directusService';
import { searchClaimQueue } from '../../../../../store/participantService';
import {
    clearValidationErrors,
    loadBusinessRecord,
    loadVacancy,
    updatePlacement
} from '../../../../../store/recruitmentService';
import { getNameFromId, getObjFromId } from '../../../../../utils/directusFunctions';
import { MAX_NAME } from '../../../../../utils/formValidation/commonConstants';
import {
    CURRENT_BUSINESS_ERROR,
    CURRENT_VACANCY_ERROR
} from '../../../../../utils/formValidation/loadingErrorMessageConstants';
import { formatFloat } from '../../../../../utils/numberUtils';
import {
    ADVISER,
    hasRole,
    MANAGER,
    QUALITY,
    RECRUITMENT_MANAGER,
    SUPERUSER
} from '../../../../../utils/userRoles';
import Button from '../../../../formElements/Button';
import DateSelect from '../../../../formElements/DateSelect';
import RadioButtons from '../../../../formElements/RadioButtons';
import SingleSelect from '../../../../formElements/SingleSelect';
import StaticField from '../../../../formElements/StaticField';
import Switch from '../../../../formElements/Switch';
import TextInputField from '../../../../formElements/TextInputField';
import BusinessContactCard from '../../../../recruitment/businesses/cards/BusinessContactCard';
import LoadingSpinner from '../../../../ui/LoadingSpinner';
import { validate } from '../validation/validateRecruitmentPlacement';

import app from '../../../../../app.module.css';
import cardClasses from '../../../../../commonStyles/cardContainer.module.css';
import form from '../../../../../commonStyles/formStyles.module.css';

const acceptedRoles = [ADVISER, MANAGER, QUALITY, RECRUITMENT_MANAGER, SUPERUSER];
const restrictedClaimsRoles = [QUALITY, SUPERUSER];
const claimEventStatusSearchValues = ['verified', 'claimed', 'paid', 'unclaimable'];
const eventTypeSearchValues = ['job outcome', 'self employment'];
const _MESSAGE = 'Job placement has been updated';

const RecruitmentPlacement = ({ row, expanded }) => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const initialState = {
        id: '',
        addressLine1: '',
        addressLine2: '',
        addressLine3: '',
        businessName: '',
        city: '',
        claimableEvent: true,
        hiringManagerEmail: '',
        employmentTypeId: '',
        endDate: '',
        endStatusId: '',
        fixedFlexibleHour: 'fixed',
        fullServiceDeclined: false,
        hiringManagerId: '',
        hiringManagerName: '',
        hiringManagerOfficeNumber: '',
        hiringManagerMobileNumber: '',
        hourlyPay: '',
        hoursPerWeek: '',
        hoursPerWeekId: '',
        jobSectorId: '',
        jobStartEvidence: false,
        participantId: '',
        postcode: '',
        startDate: '',
        vacancyId: '',
        vacancyTitle: ''
    };
    const [newEntry, setNewEntry] = useState(initialState);
    const [initialStateOnEntry, setInitialStateOnEntry] = useState(initialState);
    const [isEditable, setIsEditable] = useState(true);
    const [errors, setErrors] = useState({});
    const [isUpdateButtonDisabled, setIsUpdateButtonDisabled] = useState(false);

    // STORE STATE
    const roles = useSelector((state) => state.entities.userService.loggedInUser.roles);
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );
    const contractConstants = useSelector(
        (state) => state.entities.participantService.contractConstants
    );
    const claimQueues = useSelector((state) => state.entities.participantService.claimQueues);
    const currentBusiness = useSelector(
        (state) => state.entities.recruitmentService.currentBusiness
    );
    const currentVacancy = useSelector((state) => state.entities.recruitmentService.currentVacancy);
    const claimEventTypes = useSelector((state) => state.entities.directusService.claimEventTypes);
    const claimEventStatuses = useSelector(
        (state) => state.entities.directusService.claimEventStatuses
    );
    const hoursPerWeekDetails = useSelector(
        (state) => state.entities.directusService.hoursPerWeekDetails
    );
    const placementEndStatusDetails = useSelector(
        (state) => state.entities.directusService.placementEndStatusDetails
    );
    const placementEmploymentTypeDetails = useSelector(
        (state) => state.entities.directusService.placementEmploymentTypeDetails
    );
    const validationErrors = useSelector(
        (state) => state.entities.recruitmentService.validationErrors
    );
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);

    // USE EFFECTS
    useEffect(() => {
        claimEventTypes?.length < 1 && dispatch(loadClaimEventTypes());
        claimEventStatuses?.length < 1 && dispatch(loadClaimEventStatuses());
    }, []);

    useEffect(() => {
        if (!row || expanded === 'placementInfo-close') return;
        row?.vacancyId && dispatch(loadVacancy(row.vacancyId));
        setNewEntry(row);
        setInitialStateOnEntry(row);
    }, [row, expanded]);

    useEffect(() => {
        if (claimEventStatuses?.length < 1) return;
        const statusIds = claimEventStatuses
            .filter((el) => claimEventStatusSearchValues.includes(el.name.toLowerCase()))
            .map((el) => el.id);
        dispatch(
            searchClaimQueue(
                {
                    ptCodes: [currentParticipant.ptCode],
                    statusIds
                },
                0
            )
        );
    }, [claimEventStatuses]);

    useEffect(() => {
        const filteredClaimQueues = claimQueues.filter((el) =>
            eventTypeSearchValues.includes(el.eventType.toLowerCase())
        );

        if (
            (filteredClaimQueues.length > 0 && hasRole(restrictedClaimsRoles, roles)) ||
            (filteredClaimQueues.length === 0 && hasRole(acceptedRoles, roles))
        )
            setIsEditable(true);
        else setIsEditable(false);
    }, [claimQueues]);

    useEffect(() => {
        if (Object.keys(currentVacancy).length < 1) return;
        dispatch(loadBusinessRecord(currentVacancy.businessRecordId));
    }, [currentVacancy]);

    useEffect(() => {
        setIsUpdateButtonDisabled(Object.values(errors).some((el) => el.error));
    }, [errors]);

    useEffect(() => {
        if (successMessage === _MESSAGE) setIsUpdateButtonDisabled(false);
        if (validationErrors && Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            dispatch(clearValidationErrors());
        }
    }, [successMessage, validationErrors]);

    // HELPER FNS
    const loadingError = () => {
        if (Object.keys(currentBusiness)?.length < 1) return CURRENT_BUSINESS_ERROR;
        if (Object.keys(currentVacancy).length < 1) return CURRENT_VACANCY_ERROR;
    };

    const clearError = (key) => {
        if (key in errors && errors[key].error)
            setErrors((prev) => ({ ...prev, [key]: { error: false, message: '' } }));
    };

    // EVENT HANDLERS
    const onUpdate = (key, value) => {
        clearError(key);
        setNewEntry((prev) => ({ ...prev, [key]: value }));
    };

    const onStartDateChange = (date) => {
        clearError('endDate');
        onUpdate('startDate', date);
    };

    const onEndDateChange = (date) => {
        onUpdate('endDate', date);
        if (!date) onUpdate('endStatusId', '');
    };

    const onSwitchFixedOrFlexible = (e) => {
        clearError('hoursPerWeek');
        clearError('hoursPerWeekId');
        const { checked } = e.target;
        checked
            ? setNewEntry((prev) => ({
                  ...prev,
                  hoursPerWeek: null,
                  fixedFlexibleHour: 'flexible'
              }))
            : setNewEntry((prev) => ({ ...prev, hoursPerWeekId: '', fixedFlexibleHour: 'fixed' }));
    };

    const onSubmit = (e) => {
        e.preventDefault();
        setIsUpdateButtonDisabled(true);
        const newErrors = validate(newEntry, initialStateOnEntry);
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }
        const payload = {
            ...newEntry,
            participantId: currentParticipant.id
        };
        dispatch(updatePlacement(payload, _MESSAGE));
    };

    // AWAITING CONTENT
    const errorMsg = loadingError();
    if (errorMsg) return <LoadingSpinner content={errorMsg} />;

    // RENDER
    return (
        <div>
            <form onSubmit={onSubmit} data-testid="edit-recruitment-placement-form">
                <div className={form.formSection}>
                    <div className={form.formColumn}>
                        <StaticField
                            id="employmentTypeId"
                            label="Employment Type"
                            content={getNameFromId(
                                placementEmploymentTypeDetails,
                                row.employmentTypeId
                            )}
                            customClass="inputStyle"
                        />
                        <div className={form.formColumnSplit}>
                            <DateSelect
                                id="startDate"
                                label="Start Date"
                                disabled={!isEditable}
                                mandatory={true}
                                value={newEntry.startDate || ''}
                                error={errors.startDate}
                                onDateChange={onStartDateChange}
                            />
                            <DateSelect
                                id="endDate"
                                label="End Date"
                                disabled={!isEditable}
                                value={newEntry.endDate || ''}
                                error={errors.endDate}
                                onDateChange={onEndDateChange}
                            />
                        </div>
                        <SingleSelect
                            id="endStatusId"
                            label="End Status"
                            placeholder="Select an end status..."
                            disabled={!isEditable || !newEntry.endDate}
                            mandatory={!!newEntry.endDate}
                            menuItems={placementEndStatusDetails}
                            selectedId={newEntry.endStatusId}
                            selected={getObjFromId(placementEndStatusDetails, newEntry.endStatusId)}
                            error={errors.endStatusId}
                            onChange={(chosenId) => onUpdate('endStatusId', chosenId)}
                        />
                        <TextInputField
                            id="vacancyTitle"
                            label="Vacancy Title"
                            placeholder="Enter vacancy title"
                            disabled={!isEditable}
                            mandatory={true}
                            maxLength={MAX_NAME}
                            value={newEntry.vacancyTitle || ''}
                            error={errors.vacancyTitle}
                            onChange={(e) => onUpdate('vacancyTitle', e.target.value)}
                        />
                        <TextInputField
                            id="hourlyPay"
                            label="Hourly Pay"
                            placeholder="Enter hourly pay"
                            disabled={!isEditable}
                            mandatory={true}
                            maxLength={6}
                            value={newEntry.hourlyPay || ''}
                            error={errors.hourlyPay}
                            onChange={(e) => onUpdate('hourlyPay', formatFloat(e.target.value))}
                        />
                        <Switch
                            id="fixedFlexibleHour"
                            label="Fixed or Flexible"
                            leftLabel="Fixed"
                            rightLabel="Flexible"
                            disabled={!isEditable}
                            checked={newEntry.fixedFlexibleHour === 'flexible'}
                            onChange={onSwitchFixedOrFlexible}
                        />
                        {newEntry.fixedFlexibleHour === 'fixed' ? (
                            <TextInputField
                                id="hoursPerWeek"
                                label="Hours per Week"
                                placeholder="Enter hours"
                                disabled={!isEditable}
                                mandatory={newEntry.fixedFlexibleHour === 'fixed'}
                                maxLength={5}
                                value={newEntry.hoursPerWeek || ''}
                                error={errors.hoursPerWeek}
                                onChange={(e) =>
                                    onUpdate('hoursPerWeek', formatFloat(e.target.value))
                                }
                            />
                        ) : (
                            <SingleSelect
                                id="hoursPerWeekId"
                                label="Hours per Week"
                                placeholder="Select hours per week..."
                                disabled={!isEditable}
                                mandatory={newEntry.fixedFlexibleHour === 'flexible'}
                                menuItems={hoursPerWeekDetails}
                                selectedId={newEntry.hoursPerWeekId || ''}
                                selected={getObjFromId(
                                    hoursPerWeekDetails,
                                    newEntry.hoursPerWeekId
                                )}
                                error={errors.hoursPerWeekId}
                                onChange={(chosenId) => onUpdate('hoursPerWeekId', chosenId)}
                            />
                        )}
                    </div>
                    <div className={form.formColumn}>
                        <RadioButtons
                            id="claimableEvent"
                            label="Claimable Event?"
                            disabled={!isEditable}
                            value={newEntry.claimableEvent ?? true}
                            onChange={(option) => onUpdate('claimableEvent', option)}
                        />
                        <RadioButtons
                            id="jobStartEvidence"
                            label="Job Start Evidence?"
                            disabled={!isEditable}
                            value={newEntry.jobStartEvidence ?? false}
                            onChange={(option) => onUpdate('jobStartEvidence', option)}
                        />
                        <div hidden={contractConstants?.canIWSBeDeclined === false}>
                            <RadioButtons
                                id="fullServiceDeclined"
                                label="Full Service Declined?"
                                disabled={!isEditable}
                                value={newEntry.fullServiceDeclined ?? false}
                                onChange={(option) => onUpdate('fullServiceDeclined', option)}
                            />
                        </div>
                        <div className={app.fieldHeading}>Hiring Manager</div>
                        <div className={cardClasses.cards}>
                            <BusinessContactCard
                                entry={currentBusiness.contactCards.find(
                                    (entry) => entry.id === row.hiringManagerId
                                )}
                                roles={roles}
                                acceptedRoles={acceptedRoles}
                            />
                        </div>
                    </div>
                </div>
                <Button
                    id="update_recruitment_placement_button"
                    content="Update"
                    disabled={!isEditable || isUpdateButtonDisabled}
                    error={errors.button}
                    clearError={() => clearError('button')}
                />
            </form>
        </div>
    );
};

RecruitmentPlacement.propTypes = {
    row: PropTypes.object,
    expanded: PropTypes.string
};

export default RecruitmentPlacement;
